import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CDatePicker } from "@coreui/react-pro";
import { ArrowRight, BadgeCheck, Plus, Save, Sparkles, Trash2, Wrench } from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import ApplicantCoachPanel from "../components/applicant/ApplicantCoachPanel";
import { useApplicantCoach } from "../components/applicant/ApplicantCoachContext";
import CVSkillsBox from "../components/CVSkillsBox";
import CVVerificationReferencesBox from "../components/CVVerificationReferencesBox";
import ProfessionSuggestField from "../components/ProfessionSuggestField";
import SoftSkillsSection, { ensureCommonInit, SOFT_SKILL_KEYS } from "./ApplicantCV2_SoftSkills";
import config from "../config/config";
import KvpReportTrigger from "../components/kvp/KvpReportTrigger";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { buildAuthRequestConfig, clearStoredAuth, getStoredIdentity } from "../utils/authSession";

const allowedTabs = new Set(["overview", "cv", "skills"]);

function normalizeTab(value) {
  return allowedTabs.has(String(value || "").toLowerCase()) ? String(value).toLowerCase() : "overview";
}

function toCoachKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "application_step";
}

function getApplicationCoachLabelEn(label) {
  switch (String(label || "").trim().toLowerCase()) {
    case "skills":
      return "skills";
    case "skilltiefe":
      return "skill depth";
    case "soft skills":
      return "soft skills";
    case "sprachen":
      return "languages";
    case "berufserfahrung":
      return "work experience";
    case "ausbildung":
      return "education";
    case "profil":
      return "profile";
    case "cv":
      return "CV";
    default:
      return "application step";
  }
}

function buildEntryLabel(section, item) {
  switch (section) {
    case "language":
      return `${item.language || "Sprache"} ${item.level ? `(${item.level})` : ""}`.trim();
    case "jobs":
      return `${item.position || "Berufserfahrung"} · ${item.firmenname || "Unternehmen"}`;
    case "internship":
      return `${item.position || "Praktikum"} · ${item.firmenname || "Unternehmen"}`;
    case "occupational_education":
      return `${item.ausbildung_position || "Ausbildung"} · ${item.firmenname || "Institution"}`;
    case "school_education":
      return `${item.schule || "Schule"} ${item.hoechster_abschluss ? `· ${item.hoechster_abschluss}` : ""}`.trim();
    default:
      return "Eintrag";
  }
}

function getRecordTypeForSection(section) {
  switch (section) {
    case "language":
      return "language_entry";
    case "jobs":
      return "job_entry";
    case "internship":
      return "internship_entry";
    case "occupational_education":
      return "education_entry";
    case "school_education":
      return "school_entry";
    default:
      return "record";
  }
}

function getTabLabel(tabKey) {
  if (tabKey === "cv") return "CV";
  if (tabKey === "skills") return "Skills";
  return "Uebersicht";
}

function formatAbsoluteDate(value) {
  if (!value) return "Noch offen";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatPickerDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function parsePickerDate(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const parsed = new Date(`${text}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function countStrongSoftSkills(common) {
  return SOFT_SKILL_KEYS.filter((key) => Number(common?.[key] || 0) >= 3).length;
}

function buildReadiness(cv, skills, references) {
  const languagesReady = Array.isArray(cv.language) && cv.language.length > 0 && cv.language.every((item) => item.language && item.level);
  const experienceCount = (cv.jobs || []).length + (cv.internship || []).length;
  const educationCount = (cv.occupational_education || []).length + (cv.school_education || []).length;
  const skillsCount = skills.length;
  const averageSkill = skillsCount ? skills.reduce((sum, item) => sum + Number(item.rating || 0), 0) / skillsCount : 0;
  const strongSoftSkills = countStrongSoftSkills(cv.common || {});
  const availabilityReady = Boolean(cv.availability?.earliest_start || cv.availability?.notice_period);
  const preferencesReady = Boolean(cv.preferences?.desired_role || cv.preferences?.desired_location);

  const parts = [
    { key: "languages", label: "Sprachen", weight: 12, progress: languagesReady ? 1 : (cv.language || []).length ? 0.55 : 0, good: "Sprachen mit Niveau sind da.", missing: "Fuege mindestens eine Sprache mit Niveau hinzu.", next: "Sprache + Niveau bringen klare Punkte." },
    { key: "experience", label: "Erfahrung", weight: 18, progress: Math.min(1, experienceCount / 2), good: `${experienceCount} Eintraege zeigen deine Erfahrung.`, missing: "Mindestens eine Erfahrung fehlt noch.", next: "Eine gute Berufsstation staerkt deine Bewerbung direkt." },
    { key: "education", label: "Ausbildung", weight: 14, progress: Math.min(1, educationCount / 1), good: "Ausbildung oder Schule ist gepflegt.", missing: "Ausbildung oder Schule fehlt noch.", next: "Ein Ausbildungs-Eintrag bringt klare Vollstaendigkeit." },
    { key: "availability", label: "Verfuegbar", weight: 10, progress: availabilityReady ? 1 : 0, good: "Start oder Kuendigungsfrist sind klar.", missing: "Sag, wann du starten kannst.", next: "Mit einem Startzeitpunkt wirst du schneller einordenbar." },
    { key: "preferences", label: "Ziel", weight: 10, progress: preferencesReady ? 1 : 0.35, good: "Rolle oder Ort sind klar.", missing: "Deine Zielrolle oder dein Zielort fehlen.", next: "Ein klares Ziel verbessert deine Passung." },
    { key: "skills", label: "Skills", weight: 18, progress: Math.min(1, skillsCount / 5), good: `${skillsCount} Skills sind sichtbar.`, missing: "Du brauchst mehrere klare Skills.", next: "Drei bis fuenf starke Skills bringen schnell Punkte." },
    { key: "depth", label: "Skilltiefe", weight: 8, progress: Math.min(1, averageSkill / 4), good: "Deine Skilltiefe ist gut gepflegt.", missing: "Die Skill-Staerke ist noch zu flach.", next: "Bewerte deine wichtigsten Skills klarer." },
    { key: "soft", label: "Soft Skills", weight: 10, progress: Math.min(1, strongSoftSkills / 8), good: "Viele Soft Skills sind schon gepflegt.", missing: "Soft Skills sind noch zu duenn.", next: "Acht staerkere Soft Skills machen dein Profil runder." },
  ];

  const score = Math.round(parts.reduce((sum, part) => sum + (part.weight * part.progress), 0));
  const scoreLabel = score >= 80 ? "Sehr stark" : score >= 65 ? "Gut auf Kurs" : score >= 45 ? "Im Aufbau" : "Am Anfang";
  const scoreSummary = score >= 80
    ? "Deine Bewerbung ist klar lesbar und schon gut vorbereitet."
    : score >= 65
      ? "Deine Bewerbung ist solide. Mit wenigen Schritten wird sie deutlich staerker."
      : score >= 45
        ? "Die Basis ist da. Jetzt bringen dir die richtigen Schritte schnell neue Punkte."
        : "Deine Bewerbung braucht noch einige Kerndaten, damit Firmen dich gut einordnen koennen.";
  return {
    score,
    scoreLabel,
    scoreSummary,
    progressPercent: Math.max(0, Math.min(100, score)),
    referencesCount: references.length,
    completed: parts.filter((part) => part.progress >= 1).map((part) => part.good),
    missing: parts.filter((part) => part.progress < 1).map((part) => part.missing),
    nextSteps: parts.filter((part) => part.progress < 1).sort((left, right) => ((right.weight * (1 - right.progress)) - (left.weight * (1 - left.progress)))).slice(0, 3).map((part) => ({ label: part.label, text: part.next, points: Math.max(2, Math.round(part.weight * (1 - part.progress))) })),
    parts: parts.map((part) => ({ ...part, value: Math.round(part.progress * part.weight) })),
  };
}

function createEmptyCvState(payload) {
  const base = payload || {};
  return {
    common: ensureCommonInit(base.common || { marriage_status: "", marriage_childs: 0, skill_hobbies: "" }),
    language: base.language || [],
    jobs: base.jobs || [],
    school_education: base.school_education || [],
    occupational_education: base.occupational_education || [],
    internship: base.internship || [],
    availability: base.availability || { earliest_start: "", notice_period: "", relocation_willingness: false, remote_possible: false },
    preferences: base.preferences || { desired_role: "", desired_location: "", desired_salary_min: "", desired_salary_max: "", employment_type: "any" },
  };
}

function WorkspaceField({ label, children, hint = "" }) {
  return (
    <div className="applicant-workspace-field">
      <Label className="applicant-workspace-label">{label}</Label>
      {children}
      {hint ? <small>{hint}</small> : null}
    </div>
  );
}

function DateRangeRow({ from, to, onFrom, onTo }) {
  return (
    <div className="applicant-workspace-grid applicant-workspace-grid--split">
      <Input type="date" value={from || ""} onChange={(event) => onFrom(event.target.value)} className="applicant-field" />
      <Input type="date" value={to || ""} onChange={(event) => onTo(event.target.value)} className="applicant-field" />
    </div>
  );
}

function RepeaterSection({ sectionKey, title, items, onAdd, onRemove, renderItem, emptyText, kvpContextBase }) {
  return (
    <section className="applicant-workspace-card">
      <div className="applicant-workspace-card__head">
        <div className="applicant-workspace-heading-block">
          <span className="applicant-section-kicker">{title}</span>
          <div className="applicant-workspace-title-row">
            <h3>{title}</h3>
            <KvpReportTrigger
              context={{
                ...kvpContextBase,
                section_key: sectionKey,
                section_label: title,
              }}
            />
          </div>
        </div>
        <div className="applicant-workspace-card__head-actions">
          <Button type="button" variant="outline" className="applicant-utility-button" onClick={onAdd}><Plus size={16} /><span>Hinzufuegen</span></Button>
        </div>
      </div>
      {items.length ? (
        <Accordion type="multiple" className="applicant-workspace-accordion">
          {items.map((item, index) => (
            <AccordionItem key={`${sectionKey}-${index}`} value={`${sectionKey}-${index}`} className="applicant-workspace-accordion__item">
              <div className="applicant-workspace-accordion__trigger-row">
                <AccordionTrigger className="applicant-workspace-accordion__trigger">{buildEntryLabel(sectionKey, item)}</AccordionTrigger>
                <KvpReportTrigger
                  className="kvp-trigger-button--inline"
                  context={{
                    ...kvpContextBase,
                    section_key: sectionKey,
                    section_label: title,
                    record_type: getRecordTypeForSection(sectionKey),
                    record_id: `${sectionKey}-${index}`,
                    record_label: buildEntryLabel(sectionKey, item),
                  }}
                  currentFieldValue={buildEntryLabel(sectionKey, item)}
                />
              </div>
              <AccordionContent className="applicant-workspace-accordion__content">
                {renderItem(item, index)}
                <div className="applicant-workspace-actions applicant-workspace-actions--end">
                  <Button type="button" variant="outline" className="applicant-action-button applicant-action-button-danger" onClick={() => onRemove(index)}><Trash2 size={16} /><span>Entfernen</span></Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : <div className="applicant-empty-card">{emptyText}</div>}
    </section>
  );
}

export default function ApplicantApplicationWorkspace() {
  const navigate = useNavigate();
  const { registerCoachSurface } = useApplicantCoach();
  const userid = getStoredIdentity().userid;
  const [searchParams, setSearchParams] = useSearchParams();
  const [cv, setCv] = useState(createEmptyCvState());
  const [skills, setSkills] = useState([]);
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

  const activeTab = normalizeTab(searchParams.get("tab"));

  useEffect(() => {
    document.title = "Experts4Europe | Bewerbung";
  }, []);

  const handleAuthError = (err) => {
    if (Number(err?.response?.status) !== 401) return false;
    clearStoredAuth();
    const detail = String(err?.response?.data?.detail || err?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");
    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivitaet automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  };

  const load = async () => {
    if (!userid) {
      setError("Keine Applicant-ID gefunden.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [cvResponse, skillsResponse, referencesResponse] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/api/cv2/load/${userid}`, buildAuthRequestConfig()),
        axios.get(`${config.API_BASE_URL}/api/cv/skills`, buildAuthRequestConfig({ params: { userid } })),
        axios.get(`${config.API_BASE_URL}/api/cv/verification-references`, buildAuthRequestConfig({ params: { userid } })),
      ]);
      setCv(createEmptyCvState(cvResponse.data));
      setSkills(Array.isArray(skillsResponse.data) ? skillsResponse.data : []);
      setReferences(referencesResponse.data?.rows || []);
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.error || err?.response?.data?.message || "Die Bewerbungsseite konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const readiness = useMemo(() => buildReadiness(cv, skills, references), [cv, skills, references]);
  const primaryCoachStep = useMemo(() => {
    if (readiness.nextSteps?.length) return readiness.nextSteps[0];
    if (activeTab === "skills") {
      return { label: "Skills", text: "Halte deine wichtigsten Skills und Soft Skills aktuell.", points: 0 };
    }
    if (activeTab === "cv") {
      return { label: "CV", text: "Pruefe Sprache, Erfahrung und Ausbildung auf Vollstaendigkeit.", points: 0 };
    }
    return { label: "Bewerbung", text: "Pruefe zuerst die groessten offenen Punkte deiner Bewerbung.", points: 0 };
  }, [activeTab, readiness.nextSteps]);
  const kvpBaseContext = useMemo(() => ({
    module_key: "application",
    module_label: "Bewerbung",
    tab_key: activeTab,
    tab_label: getTabLabel(activeTab),
    last_error_code: error ? "application_workspace_error" : null,
  }), [activeTab, error]);
  const searchItems = useMemo(() => ([
    { href: "/dashboard/applicant", label: "Dashboard", description: "Zur Mission Control" },
    { href: "/dashboard/applicant/bewerbung?tab=cv", label: "CV", description: "Berufserfahrung, Ausbildung und Sprache" },
    { href: "/dashboard/applicant/bewerbung?tab=skills", label: "Skills", description: "Skills und Soft Skills staerken" },
    { href: "/dashboard/applicant/sprachtest-b1", label: "Sprachtest B1", description: "Deutsch-Test weiterfuehren" },
  ]), []);
  const coachSurfaceContext = useMemo(() => {
    const topNextSteps = (readiness.nextSteps || []).slice(0, 3);
    const nextStepHref = primaryCoachStep.label === "Skills" || primaryCoachStep.label === "Skilltiefe" || primaryCoachStep.label === "Soft Skills"
      ? "/dashboard/applicant/bewerbung?tab=skills"
      : "/dashboard/applicant/bewerbung?tab=cv";
    return {
      screen_key: "applicant_application_workspace",
      screen_label: "Bewerbung",
      highlighted_step: `${primaryCoachStep.label}: ${primaryCoachStep.text}`,
      surface_state_key: `application:${activeTab}:${loading ? "loading" : readiness.score}:${readiness.missing.length}:${primaryCoachStep.label}`,
      surface_summary: {
        surface_type: "application",
        active_tab: activeTab,
        status_label: loading ? "Wird geladen" : readiness.scoreLabel,
        status_value: loading ? null : readiness.score,
        status_note: loading ? "Die Bewerbungsdaten werden gerade geladen." : readiness.scoreSummary,
        completed_count: readiness.completed.length,
        missing_count: readiness.missing.length,
        skill_count: skills.length,
        references_count: references.length,
        language_entry_count: (cv.language || []).length,
        job_count: (cv.jobs || []).length,
        education_count: (cv.occupational_education || []).length + (cv.school_education || []).length,
        coach_next_step: {
          key: `application_${toCoachKey(primaryCoachStep.label)}`,
          label_de: primaryCoachStep.label,
          label_en: getApplicationCoachLabelEn(primaryCoachStep.label),
          reason_de: primaryCoachStep.text,
          href: nextStepHref,
        },
        coach_missing_items: topNextSteps.map((item) => ({
          key: `application_${toCoachKey(item.label)}`,
          label_de: item.label,
          label_en: getApplicationCoachLabelEn(item.label),
          reason_de: item.text,
          href: item.label === "Skills" || item.label === "Skilltiefe" || item.label === "Soft Skills"
            ? "/dashboard/applicant/bewerbung?tab=skills"
            : "/dashboard/applicant/bewerbung?tab=cv",
        })),
      },
    };
  }, [activeTab, cv.jobs, cv.language, cv.occupational_education, cv.school_education, loading, primaryCoachStep, readiness.completed.length, readiness.missing.length, readiness.nextSteps, readiness.score, readiness.scoreLabel, readiness.scoreSummary, references.length, skills.length]);

  useEffect(() => {
    registerCoachSurface(coachSurfaceContext);
  }, [coachSurfaceContext, registerCoachSurface]);

  const applicationStatusBand = [
    {
      label: "Bewerbungsstand",
      value: loading ? "Wird geladen" : `${readiness.scoreLabel} (${readiness.score}/100)`,
      note: loading ? "Die Bewerbungsdaten werden gerade geordnet geladen." : readiness.scoreSummary,
    },
    {
      label: "Naechster Schwerpunkt",
      value: primaryCoachStep.label || "Bewerbung",
      note: primaryCoachStep.text || "Hier liegt der naechste sinnvolle Bearbeitungsschritt.",
    },
    {
      label: "Fruehester Start",
      value: cv.availability?.earliest_start ? formatAbsoluteDate(cv.availability.earliest_start) : "Noch offen",
      note: cv.availability?.notice_period ? `Kuendigungsfrist: ${cv.availability.notice_period}` : "Ein Startdatum oder eine Kuendigungsfrist verbessert die Einordnung.",
    },
    {
      label: "Nachweise",
      value: `${readiness.referencesCount} Referenzen`,
      note: references.length ? "Dokumente und Referenzen sind direkt im selben Arbeitsraum erreichbar." : "Lege Nachweise frueh an, damit spaetere Pruefung nicht stockt.",
    },
  ];

  const setTab = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "overview") next.delete("tab");
    else next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  const setSection = (section, value) => setCv((current) => ({ ...current, [section]: value }));
  const patchArrayItem = (section, index, key, value) => setCv((current) => {
    const next = [...(current[section] || [])];
    next[index] = { ...(next[index] || {}), [key]: value };
    return { ...current, [section]: next };
  });
  const addArrayItem = (section, template) => setCv((current) => ({ ...current, [section]: [...(current[section] || []), template] }));
  const removeArrayItem = (section, index) => setCv((current) => ({ ...current, [section]: (current[section] || []).filter((_, idx) => idx !== index) }));

  const saveCv = async () => {
    if (!userid) return;
    setSaving(true);
    setNotice("");
    setError("");
    try {
      await axios.put(`${config.API_BASE_URL}/api/cv2/save`, { userid, ...cv }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setNotice("Bewerbung gespeichert.");
      await load();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.error || err?.response?.data?.message || "Die Bewerbung konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ApplicantShell
      title="Bewerbung"
      subtitle="Hier siehst du deinen Bewerbungsstand und verbesserst CV und Skills direkt."
      actions={(
        <Button className="applicant-cta-button" onClick={saveCv} disabled={saving || loading}>
          {saving ? "Speichert..." : "Bewerbung speichern"}
        </Button>
      )}
      notifications={[]}
      searchItems={searchItems}
    >
      <div className="applicant-workspace-page applicant-workspace-page--application">
        {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}
        {notice ? <div className="dashboard-inline-alert dashboard-inline-alert-success">{notice}</div> : null}
        <section className="e4e-status-band applicant-application__status-band">
          <div className="e4e-status-band__grid">
            {applicationStatusBand.map((item) => (
              <article key={item.label} className="e4e-status-band__item">
                <span className="e4e-status-band__label">{item.label}</span>
                <strong className="e4e-status-band__value">{item.value}</strong>
                <small className="e4e-status-band__note">{item.note}</small>
              </article>
            ))}
          </div>
        </section>

        <section className="applicant-application-stage">
          <div className="applicant-application-stage__main">
            <div className="applicant-workspace-card__head applicant-workspace-card__head--inline">
              <div className="applicant-workspace-heading-block">
                <span className="applicant-section-kicker">Bewerbungsstand</span>
                <div className="applicant-workspace-title-row">
                  <h2>Wie stark ist deine Bewerbung?</h2>
                  <KvpReportTrigger
                    context={{
                      ...kvpBaseContext,
                      section_key: "overview_stage",
                      section_label: "Bewerbungsstand",
                    }}
                  />
                </div>
                <p>Du siehst hier, was schon fertig ist, was dir noch fehlt und was dir als Naechstes Punkte bringt.</p>
              </div>
            </div>
            <div className="applicant-application-score">
              <div className="applicant-readiness-badge" aria-label={`Bewerbungs-Score ${loading ? "wird geladen" : readiness.score}`}>
                <strong>{loading ? "--" : readiness.score}</strong>
                <span>von 100</span>
              </div>
              <div className="applicant-application-score__copy">
                <div className="applicant-application-score__head">
                  <strong>{loading ? "Dein Score wird geladen" : readiness.scoreLabel}</strong>
                  <span>{loading ? "Wir holen gerade deine Daten." : readiness.scoreSummary}</span>
                </div>
                <div className="applicant-progress-meter" aria-hidden="true">
                  <div className="applicant-progress-meter__bar" style={{ width: `${loading ? 12 : readiness.progressPercent}%` }} />
                </div>
                <div className="applicant-workspace-chip-row">
                  <span className="applicant-status-pill"><BadgeCheck size={15} />{readiness.completed.length} fertig</span>
                  <span className="applicant-status-pill"><Wrench size={15} />{readiness.missing.length} offen</span>
                  <span className="applicant-status-pill"><Sparkles size={15} />{skills.length} Skills</span>
                  <span className="applicant-status-pill">{readiness.referencesCount} Nachweise</span>
                </div>
              </div>
            </div>
            <div className="applicant-workspace-scoreboard applicant-workspace-scoreboard--application">
              {readiness.parts.map((part) => (
                <article key={part.key} className="applicant-signal-tile applicant-signal-tile--application">
                  <div className="applicant-signal-tile__top">
                    <span>{part.label}</span>
                    <strong>{part.value}/{part.weight}</strong>
                  </div>
                  <div className="applicant-mini-meter" aria-hidden="true">
                    <div className="applicant-mini-meter__bar" style={{ width: `${Math.max(8, Math.round(part.progress * 100))}%` }} />
                  </div>
                  <small>{part.progress >= 1 ? part.good : part.missing}</small>
                </article>
              ))}
            </div>
          </div>
          <aside className="applicant-application-stage__side">
            <section className="applicant-workspace-card applicant-workspace-card--priority">
              <div className="applicant-workspace-card__head">
                <div>
                  <span className="applicant-section-kicker">Naechste Schritte</span>
                  <h3>Was bringt dir jetzt Punkte?</h3>
                </div>
                <KvpReportTrigger
                  context={{
                    ...kvpBaseContext,
                    section_key: "next_steps",
                    section_label: "Naechste Schritte",
                  }}
                />
              </div>
              <div className="applicant-next-step-stack">
                {readiness.nextSteps.length ? readiness.nextSteps.map((item) => (
                  <button key={item.label} type="button" className="applicant-action-list__item" onClick={() => setTab(item.label === "Skills" || item.label === "Skilltiefe" || item.label === "Soft Skills" ? "skills" : "cv")}>
                    <div>
                      <strong>{item.label}</strong>
                      <span>{item.text}</span>
                    </div>
                    <small>+{item.points}</small>
                  </button>
                )) : <div className="applicant-empty-card">Deine Bewerbung ist schon sehr weit. Pruefe jetzt nur noch Details und speichere sauber ab.</div>}
              </div>
            </section>
            <section className="applicant-workspace-card applicant-workspace-card--summary">
              <div className="applicant-workspace-card__head">
                <div>
                  <span className="applicant-section-kicker">Schnellueberblick</span>
                  <h3>Was ist fertig?</h3>
                </div>
                <KvpReportTrigger
                  context={{
                    ...kvpBaseContext,
                    section_key: "quick_summary",
                    section_label: "Schnellueberblick",
                  }}
                />
              </div>
              <div className="applicant-readiness-panel__meta applicant-readiness-panel__meta--single">
                <div><span>Fertig</span><strong>{readiness.completed.length} Bereiche</strong></div>
                <div><span>Offen</span><strong>{readiness.missing.length} Bereiche</strong></div>
                <div><span>Direkt bearbeiten</span><strong>CV, Skills, Nachweise</strong></div>
              </div>
            </section>
          </aside>
        </section>

        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList className="applicant-tablist applicant-tablist-secondary">
            <TabsTrigger value="overview" className="applicant-tabtrigger">Uebersicht</TabsTrigger>
            <TabsTrigger value="cv" className="applicant-tabtrigger">CV</TabsTrigger>
            <TabsTrigger value="skills" className="applicant-tabtrigger">Skills</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="applicant-tabcontent">
            <div className="applicant-workspace-grid applicant-workspace-grid--main">
              <section className="applicant-workspace-card">
                <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Schon geschafft</span><h3>Das ist schon da</h3></div><KvpReportTrigger context={{ ...kvpBaseContext, section_key: "completed_overview", section_label: "Schon geschafft" }} /></div>
                {readiness.completed.length ? <div className="applicant-checklist">{readiness.completed.map((item) => <div key={item} className="applicant-checklist__item is-done"><BadgeCheck size={16} /><span>{item}</span></div>)}</div> : <div className="applicant-empty-card">Dein erster Eintrag bringt direkt Bewegung in deine Bewerbung.</div>}
              </section>
              <section className="applicant-workspace-card">
                <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Fehlt noch</span><h3>So wirst du staerker</h3></div><KvpReportTrigger context={{ ...kvpBaseContext, section_key: "missing_overview", section_label: "Fehlt noch" }} /></div>
                <div className="applicant-checklist">{readiness.missing.map((item) => <div key={item} className="applicant-checklist__item is-open"><ArrowRight size={16} /><span>{item}</span></div>)}</div>
              </section>
              <section className="applicant-workspace-card applicant-workspace-card--span-2">
                <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Naechste Punkte</span><h3>Das bringt dir jetzt am meisten</h3></div><KvpReportTrigger context={{ ...kvpBaseContext, section_key: "next_points", section_label: "Naechste Punkte" }} /></div>
                <div className="applicant-next-step-stack">{readiness.nextSteps.map((item) => <button key={item.label} type="button" className="applicant-action-list__item" onClick={() => setTab(item.label === "Skills" || item.label === "Skilltiefe" || item.label === "Soft Skills" ? "skills" : "cv")}><div><strong>{item.label}</strong><span>{item.text}</span></div><small>+{item.points} Punkte</small></button>)}</div>
              </section>
            </div>
          </TabsContent>

          <TabsContent value="cv" className="applicant-tabcontent">
            <div className="applicant-workspace-stack">
              <section className="applicant-workspace-card">
                <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Basis</span><h3>Sprache, Ziel und Verfuegbarkeit</h3></div><KvpReportTrigger context={{ ...kvpBaseContext, section_key: "basics", section_label: "Sprache, Ziel und Verfuegbarkeit" }} /></div>
                <div className="applicant-workspace-grid applicant-workspace-grid--double">
                  <div className="applicant-workspace-subcard">
                    <WorkspaceField label="Fruehester Start">
                      <CDatePicker
                        className="applicant-field"
                        date={cv.availability?.earliest_start || null}
                        inputDateFormat={formatPickerDate}
                        inputDateParse={parsePickerDate}
                        locale="de-DE"
                        onDateChange={(_date, formattedDate) => setSection("availability", { ...(cv.availability || {}), earliest_start: formattedDate || "" })}
                        placeholder="TT.MM.JJJJ"
                      />
                    </WorkspaceField>
                    <WorkspaceField label="Kuendigungsfrist"><Input value={cv.availability?.notice_period || ""} onChange={(event) => setSection("availability", { ...(cv.availability || {}), notice_period: event.target.value })} className="applicant-field" placeholder="z. B. 4 Wochen" /></WorkspaceField>
                    <div className="applicant-switch-row"><label><Switch checked={!!cv.availability?.relocation_willingness} onCheckedChange={(checked) => setSection("availability", { ...(cv.availability || {}), relocation_willingness: checked })} /><span>Umzug moeglich</span></label><label><Switch checked={!!cv.availability?.remote_possible} onCheckedChange={(checked) => setSection("availability", { ...(cv.availability || {}), remote_possible: checked })} /><span>Remote moeglich</span></label></div>
                  </div>
                  <div className="applicant-workspace-subcard">
                            <WorkspaceField label="Gewuenschter Beruf" hint="Tippe z. B. Pflege, um passende Berufsgruppen vorgeschlagen zu bekommen."><ProfessionSuggestField value={cv.preferences?.desired_role || ""} onChange={(value) => setSection("preferences", { ...(cv.preferences || {}), desired_role: value })} className="applicant-field pr-10" placeholder="z. B. Pflege oder Pflegefachkraft" /></WorkspaceField>
                    <WorkspaceField label="Zielort"><Input value={cv.preferences?.desired_location || ""} onChange={(event) => setSection("preferences", { ...(cv.preferences || {}), desired_location: event.target.value })} className="applicant-field" placeholder="z. B. Berlin oder NRW" /></WorkspaceField>
                    <WorkspaceField label="Arbeitsform"><Select value={cv.preferences?.employment_type || "any"} onValueChange={(value) => setSection("preferences", { ...(cv.preferences || {}), employment_type: value })}><SelectTrigger className="applicant-field"><SelectValue placeholder="Waehlen" /></SelectTrigger><SelectContent><SelectItem value="full_time">Vollzeit</SelectItem><SelectItem value="part_time">Teilzeit</SelectItem><SelectItem value="freelance">Freelance</SelectItem><SelectItem value="internship">Praktikum</SelectItem><SelectItem value="any">Egal</SelectItem></SelectContent></Select></WorkspaceField>
                  </div>
                </div>
              </section>

              <RepeaterSection sectionKey="language" title="Sprachen" items={cv.language || []} kvpContextBase={kvpBaseContext} onAdd={() => addArrayItem("language", { language: "", level: "" })} onRemove={(index) => removeArrayItem("language", index)} emptyText="Noch keine Sprache eingetragen." renderItem={(item, index) => <div className="applicant-workspace-grid applicant-workspace-grid--double"><WorkspaceField label="Sprache"><Input value={item.language || ""} onChange={(event) => patchArrayItem("language", index, "language", event.target.value)} className="applicant-field" /></WorkspaceField><WorkspaceField label="Niveau"><Select value={item.level || ""} onValueChange={(value) => patchArrayItem("language", index, "level", value)}><SelectTrigger className="applicant-field"><SelectValue placeholder="A1 bis C2" /></SelectTrigger><SelectContent>{["A1", "A2", "B1", "B2", "C1", "C2", "Muttersprache"].map((level) => <SelectItem key={level} value={level}>{level}</SelectItem>)}</SelectContent></Select></WorkspaceField></div>} />
              <RepeaterSection sectionKey="jobs" title="Berufserfahrung" items={cv.jobs || []} kvpContextBase={kvpBaseContext} onAdd={() => addArrayItem("jobs", { von: "", bis: "", firmenname: "", position: "", taetigkeit: "" })} onRemove={(index) => removeArrayItem("jobs", index)} emptyText="Noch keine Berufserfahrung eingetragen." renderItem={(item, index) => <div className="applicant-workspace-stack"><DateRangeRow from={item.von} to={item.bis} onFrom={(value) => patchArrayItem("jobs", index, "von", value)} onTo={(value) => patchArrayItem("jobs", index, "bis", value)} /><div className="applicant-workspace-grid applicant-workspace-grid--double"><WorkspaceField label="Unternehmen"><Input value={item.firmenname || ""} onChange={(event) => patchArrayItem("jobs", index, "firmenname", event.target.value)} className="applicant-field" /></WorkspaceField><WorkspaceField label="Rolle"><Input value={item.position || ""} onChange={(event) => patchArrayItem("jobs", index, "position", event.target.value)} className="applicant-field" /></WorkspaceField></div><WorkspaceField label="Was hast du dort gemacht?"><Textarea rows={4} value={item.taetigkeit || ""} onChange={(event) => patchArrayItem("jobs", index, "taetigkeit", event.target.value)} className="applicant-field" /></WorkspaceField></div>} />
              <RepeaterSection sectionKey="internship" title="Praktika" items={cv.internship || []} kvpContextBase={kvpBaseContext} onAdd={() => addArrayItem("internship", { von: "", bis: "", firmenname: "", position: "", beschreibung: "" })} onRemove={(index) => removeArrayItem("internship", index)} emptyText="Noch kein Praktikum eingetragen." renderItem={(item, index) => <div className="applicant-workspace-stack"><DateRangeRow from={item.von} to={item.bis} onFrom={(value) => patchArrayItem("internship", index, "von", value)} onTo={(value) => patchArrayItem("internship", index, "bis", value)} /><div className="applicant-workspace-grid applicant-workspace-grid--double"><WorkspaceField label="Unternehmen"><Input value={item.firmenname || ""} onChange={(event) => patchArrayItem("internship", index, "firmenname", event.target.value)} className="applicant-field" /></WorkspaceField><WorkspaceField label="Rolle"><Input value={item.position || ""} onChange={(event) => patchArrayItem("internship", index, "position", event.target.value)} className="applicant-field" /></WorkspaceField></div><WorkspaceField label="Kurzbeschreibung"><Textarea rows={3} value={item.beschreibung || ""} onChange={(event) => patchArrayItem("internship", index, "beschreibung", event.target.value)} className="applicant-field" /></WorkspaceField></div>} />
              <RepeaterSection sectionKey="occupational_education" title="Ausbildung" items={cv.occupational_education || []} kvpContextBase={kvpBaseContext} onAdd={() => addArrayItem("occupational_education", { von: "", bis: "", firmenname: "", ausbildung_position: "", abschluss: "" })} onRemove={(index) => removeArrayItem("occupational_education", index)} emptyText="Noch keine Ausbildung oder kein Studium eingetragen." renderItem={(item, index) => <div className="applicant-workspace-stack"><DateRangeRow from={item.von} to={item.bis} onFrom={(value) => patchArrayItem("occupational_education", index, "von", value)} onTo={(value) => patchArrayItem("occupational_education", index, "bis", value)} /><div className="applicant-workspace-grid applicant-workspace-grid--double"><WorkspaceField label="Institution"><Input value={item.firmenname || ""} onChange={(event) => patchArrayItem("occupational_education", index, "firmenname", event.target.value)} className="applicant-field" /></WorkspaceField><WorkspaceField label="Ausbildung oder Studium"><Input value={item.ausbildung_position || ""} onChange={(event) => patchArrayItem("occupational_education", index, "ausbildung_position", event.target.value)} className="applicant-field" /></WorkspaceField></div><WorkspaceField label="Abschluss"><Input value={item.abschluss || ""} onChange={(event) => patchArrayItem("occupational_education", index, "abschluss", event.target.value)} className="applicant-field" /></WorkspaceField></div>} />
              <RepeaterSection sectionKey="school_education" title="Schulbildung" items={cv.school_education || []} kvpContextBase={kvpBaseContext} onAdd={() => addArrayItem("school_education", { von: "", bis: "", schule: "", hoechster_abschluss: "" })} onRemove={(index) => removeArrayItem("school_education", index)} emptyText="Noch keine Schulbildung eingetragen." renderItem={(item, index) => <div className="applicant-workspace-stack"><DateRangeRow from={item.von} to={item.bis} onFrom={(value) => patchArrayItem("school_education", index, "von", value)} onTo={(value) => patchArrayItem("school_education", index, "bis", value)} /><div className="applicant-workspace-grid applicant-workspace-grid--double"><WorkspaceField label="Schule"><Input value={item.schule || ""} onChange={(event) => patchArrayItem("school_education", index, "schule", event.target.value)} className="applicant-field" /></WorkspaceField><WorkspaceField label="Abschluss"><Input value={item.hoechster_abschluss || ""} onChange={(event) => patchArrayItem("school_education", index, "hoechster_abschluss", event.target.value)} className="applicant-field" /></WorkspaceField></div></div>} />
              <section className="applicant-workspace-card"><div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Nachweise</span><h3>Dokumente und Referenzen</h3></div><KvpReportTrigger context={{ ...kvpBaseContext, section_key: "references", section_label: "Dokumente und Referenzen" }} /></div><CVVerificationReferencesBox userid={userid} /></section>
              <div className="applicant-workspace-actions applicant-workspace-actions--end"><Button className="applicant-cta-button" onClick={saveCv} disabled={saving}><Save size={16} /><span>{saving ? "Speichert..." : "CV speichern"}</span></Button></div>
            </div>
          </TabsContent>

          <TabsContent value="skills" className="applicant-tabcontent">
            <div className="applicant-workspace-stack">
              <section className="applicant-workspace-card"><div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Skills</span><h3>Was du kannst</h3></div><KvpReportTrigger context={{ ...kvpBaseContext, section_key: "skills", section_label: "Was du kannst" }} /></div><CVSkillsBox userid={userid} /></section>
              <section className="applicant-workspace-card"><div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Soft Skills</span><h3>Wie du arbeitest</h3></div><KvpReportTrigger context={{ ...kvpBaseContext, section_key: "soft_skills", section_label: "Wie du arbeitest" }} /></div><SoftSkillsSection common={cv.common} onChange={(updated) => setSection("common", updated)} /></section>
              <div className="applicant-workspace-actions applicant-workspace-actions--end"><Button className="applicant-cta-button" onClick={saveCv} disabled={saving}><Save size={16} /><span>{saving ? "Speichert..." : "Skills speichern"}</span></Button></div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/")}>Startseite</Button>
            <Button className="applicant-cta-button" onClick={() => navigate("/login", { replace: true })}>Erneut anmelden</Button>
          </div>
        </DialogContent>
      </Dialog>
      <ApplicantCoachPanel />
    </ApplicantShell>
  );
}
